import React, { useState } from 'react';

const ContaGlobal: React.FC = () => {
  const [valorMoeda, setValorMoeda] = useState('2000');
  const [moeda, setMoeda] = useState<'USD' | 'EUR' | 'GBP'>('USD');
  const [cotacaoComercial, setCotacaoComercial] = useState('5.15');

  const handleMoedaChange = (novaMoeda: 'USD' | 'EUR' | 'GBP') => {
    setMoeda(novaMoeda);
    if (novaMoeda === 'USD') setCotacaoComercial('5.15');
    if (novaMoeda === 'EUR') setCotacaoComercial('5.60');
    if (novaMoeda === 'GBP') setCotacaoComercial('6.55');
  };

  const calcular = () => {
    const qtd = parseFloat(valorMoeda) || 0;
    const cambio = parseFloat(cotacaoComercial) || 0;
    if (qtd <= 0 || cambio <= 0) return null;

    // Wise: ~0.9% spread médio + 1.1% IOF
    const custoWise = qtd * cambio * (1 + 0.009) * (1 + 0.011);

    // Nomad: ~1.8% spread padrão (1% no Nomad Pass nível avançado, usamos 1.5% médio) + 1.1% IOF
    const custoNomad = qtd * cambio * (1 + 0.015) * (1 + 0.011);

    // Cartão de Crédito Tradicional: ~4.5% spread bancário (dólar turismo) + IOF cartão internacional
    const custoCartaoTradicional = qtd * cambio * (1 + 0.045) * (1 + 0.0338);

    const economiaWise = custoCartaoTradicional - custoWise;
    const economiaNomad = custoCartaoTradicional - custoNomad;

    return {
      custoWise,
      custoNomad,
      custoCartaoTradicional,
      economiaWise,
      economiaNomad
    };
  };

  const res = calcular();
  const formatBRL = (val: number) =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">
        Calculadora de Câmbio: Wise vs. Nomad vs. Cartão de Crédito
      </h3>
      <p className="text-gray-600 text-sm mb-6">
        Simule quanto você vai pagar em Reais na sua viagem internacional comparando o câmbio comercial + IOF reduzido das contas globais contra o cartão de crédito tradicional.
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Moeda da Viagem
              <select
                value={moeda}
                onChange={(e) => handleMoedaChange(e.target.value as 'USD' | 'EUR' | 'GBP')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
              >
                <option value="USD">🇺🇸 Dólar Americano (USD)</option>
                <option value="EUR">🇪🇺 Euro (EUR)</option>
                <option value="GBP">🇬🇧 Libra Esterlina (GBP)</option>
              </select>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor da viagem em {moeda}
              <input
                type="number"
                value={valorMoeda}
                onChange={(e) => setValorMoeda(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
                placeholder="2000"
              />
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cotação Comercial Hoje (R$)
              <input
                type="number"
                step="0.05"
                value={cotacaoComercial}
                onChange={(e) => setCotacaoComercial(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
              />
            </label>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Custo Total para {moeda} {Number(valorMoeda || 0).toLocaleString('pt-BR')}
          </h4>
          {res ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div>
                  <span className="font-bold text-emerald-900 block">🌍 Wise (Multimoedas)</span>
                  <span className="text-xs text-emerald-700">Câmbio comercial + IOF 1,1% + menor spread</span>
                </div>
                <span className="font-bold text-emerald-800 text-lg">{formatBRL(res.custoWise)}</span>
              </div>

              <div className="flex justify-between items-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div>
                  <span className="font-bold text-blue-900 block">🇺🇸 Nomad (Foco EUA + Sala VIP)</span>
                  <span className="text-xs text-blue-700">Câmbio comercial + IOF 1,1% + Nomad Pass</span>
                </div>
                <span className="font-bold text-blue-800 text-lg">{formatBRL(res.custoNomad)}</span>
              </div>

              <div className="flex justify-between items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                <div>
                  <span className="font-bold text-red-900 block">💳 Cartão de Crédito Tradicional</span>
                  <span className="text-xs text-red-700">Dólar turismo (spread ~4,5%) + IOF Cartão</span>
                </div>
                <span className="font-bold text-red-700 text-lg">{formatBRL(res.custoCartaoTradicional)}</span>
              </div>

              <div className="pt-2 text-center">
                <p className="text-sm font-bold text-gray-900 mb-3">
                  🎉 Você economiza até <span className="text-emerald-600">{formatBRL(res.economiaWise)}</span> usando Conta Global!
                </p>
                <a
                  href="https://wise.prf.hn/click/camref:1110lw5ki"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
                >
                  Abrir Conta Gratuita na Wise →
                </a>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Informe o valor e a cotação para calcular sua economia.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContaGlobal;
